/**
 * Frontend service for the stripe-billing Medplum Bot.
 *
 * Wraps `medplum.executeBot()` with typed inputs/outputs.
 * Follows the same pattern as adminActionsService.ts.
 *
 * The Bot must be registered in Medplum with:
 *   identifier: { system: 'https://unify.health/bots', value: 'stripe-billing' }
 *   runtimeVersion: 'vmcontext'
 *
 * Required project secrets: STRIPE_SECRET_KEY, STRIPE_PUBLISHABLE_KEY
 */

import { ContentType } from '@medplum/core';
import type { MedplumClient } from '@medplum/core';
import type { Identifier } from '@medplum/fhirtypes';

// ---------------------------------------------------------------------------
// Bot identifier
// ---------------------------------------------------------------------------

export const STRIPE_BILLING_BOT_IDENTIFIER = {
  system: 'https://unify.health/bots',
  value: 'stripe-billing',
} as const;

export function getStripeBillingBotTarget(): string | Identifier {
  const id =
    typeof import.meta !== 'undefined'
      ? (import.meta.env as Record<string, string | undefined> | undefined)?.VITE_MEDPLUM_STRIPE_BILLING_BOT_ID?.trim()
      : undefined;
  if (id) return id;
  return STRIPE_BILLING_BOT_IDENTIFIER;
}

// ---------------------------------------------------------------------------
// Shared types (match bot handler expectations)
// ---------------------------------------------------------------------------

export interface SavedPaymentMethod {
  id: string;
  userId: string;
  stripeCustomerId: string;
  stripePaymentMethodId: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  cardholderName: string;
  funding: string;
  isDefault: boolean;
  nickname: string;
  createdAt: string;
  updatedAt: string;
}

export interface StripeBillingConfig {
  publishableKey: string;
  hasSecretKey: boolean;
  hasPublishableKey: boolean;
}

export interface PayInvoiceResult {
  success: boolean;
  paymentIntentId: string;
  status: string;
  clientSecret?: string;
}

// ---------------------------------------------------------------------------
// Generic bot executor
// ---------------------------------------------------------------------------

async function executeBillingAction<T>(
  medplum: MedplumClient,
  input: Record<string, unknown>,
): Promise<T> {
  return medplum.executeBot(
    getStripeBillingBotTarget(),
    input,
    ContentType.JSON,
  ) as Promise<T>;
}

// ---------------------------------------------------------------------------
// Typed action wrappers
// ---------------------------------------------------------------------------

export async function getStripeConfig(
  medplum: MedplumClient,
): Promise<StripeBillingConfig> {
  return executeBillingAction(medplum, { action: 'get-config' });
}

export async function createSetupIntent(
  medplum: MedplumClient,
  userId: string,
  email?: string,
  name?: string,
): Promise<{ clientSecret: string; stripeCustomerId: string; publishableKey: string }> {
  return executeBillingAction(medplum, {
    action: 'create-setup-intent',
    userId,
    email,
    name,
  });
}

export async function syncPaymentMethod(
  medplum: MedplumClient,
  params: {
    userId: string;
    setupIntentId: string;
    nickname?: string;
    makeDefault?: boolean;
  },
): Promise<SavedPaymentMethod> {
  return executeBillingAction(medplum, {
    action: 'sync-payment-method',
    ...params,
  });
}

export async function listPaymentMethods(
  medplum: MedplumClient,
  userId: string,
): Promise<SavedPaymentMethod[]> {
  return executeBillingAction(medplum, {
    action: 'list-payment-methods',
    userId,
  });
}

export async function detachPaymentMethod(
  medplum: MedplumClient,
  userId: string,
  stripePaymentMethodId: string,
): Promise<void> {
  await executeBillingAction(medplum, {
    action: 'detach-payment-method',
    userId,
    stripePaymentMethodId,
  });
}

export async function setDefaultPaymentMethod(
  medplum: MedplumClient,
  userId: string,
  stripePaymentMethodId: string,
): Promise<void> {
  await executeBillingAction(medplum, {
    action: 'set-default-payment-method',
    userId,
    stripePaymentMethodId,
  });
}

export async function payInvoice(
  medplum: MedplumClient,
  params: {
    userId: string;
    amountCents: number;
    currency?: string;
    description?: string;
    paymentMethodId?: string;
  },
): Promise<PayInvoiceResult> {
  return executeBillingAction(medplum, {
    action: 'pay-invoice',
    ...params,
  });
}
